import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { OptionSets } from '@/types'
import Button from '@/components/ui/Button'
import Card, { SectionLabel } from '@/components/ui/Card'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Toggle from '@/components/ui/Toggle'
import { useToast } from '@/components/ui/toast'
import type { Draft, PatchDraft } from './draft'

/** Name, category, sizes, weight and whether the shop shows it. */
export default function DetailsSection({ draft, patch }: { draft: Draft; patch: PatchDraft }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  // null = picking from the dropdown; a string = the in-progress "Add category" input value.
  const [addingCategory, setAddingCategory] = useState<string | null>(null)

  // Categories only: the garment groups come from Product Options.
  const optionSets = useQuery({
    queryKey: ['options'],
    queryFn: async () => (await api.get<{ data: OptionSets }>('/options')).data.data,
  })

  const addCategory = useMutation({
    mutationFn: async (name: string) =>
      (await api.post<{ message: string; value: string; data: string[] }>('/admin/categories', { name })).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['options'] })
      // res.value is the canonical spelling: a duplicate in another casing
      // selects the existing category instead of erroring.
      patch({ category: res.value })
      setAddingCategory(null)
      toast.success(res.message)
    },
    onError: (err) => toast.error('Could not add category', errorMessage(err)),
  })

  // A legacy category not (yet) in the managed set still shows and keeps its
  // value; it also bridges the refetch gap right after adding a new one.
  const categoryValues = optionSets.data?.categories ?? []
  const categoryOptions = (
    draft.category && !categoryValues.includes(draft.category) ? [...categoryValues, draft.category] : categoryValues
  ).map((v) => ({ value: v, label: v }))

  return (
    <Card>
      <SectionLabel>Details</SectionLabel>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <Input value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
          </Field>
          <Field label="Category">
            {addingCategory === null ? (
              <Select
                value={draft.category}
                onChange={(v) => patch({ category: v })}
                options={categoryOptions}
                placeholder="Select category"
                action={{ label: 'Add category', onSelect: () => setAddingCategory('') }}
              />
            ) : (
              <div className="flex items-center gap-1.5">
                <Input
                  autoFocus
                  value={addingCategory}
                  maxLength={100}
                  placeholder="New category name"
                  onChange={(e) => setAddingCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && addingCategory.trim()) addCategory.mutate(addingCategory.trim())
                    if (e.key === 'Escape') setAddingCategory(null)
                  }}
                />
                <Button
                  loading={addCategory.isPending}
                  disabled={!addingCategory.trim()}
                  onClick={() => addCategory.mutate(addingCategory.trim())}
                  className="h-10 px-3"
                  aria-label="Save category"
                >
                  <Check className="size-4" />
                </Button>
                <Button variant="ghost" onClick={() => setAddingCategory(null)} className="h-10 px-2" aria-label="Cancel">
                  <X className="size-4" />
                </Button>
              </div>
            )}
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Sizes (comma separated)">
            <Input value={draft.sizes} onChange={(e) => patch({ sizes: e.target.value })} />
          </Field>
          <Field label="Weight per piece (g)">
            <Input
              type="number"
              min={1}
              value={draft.weight_grams}
              onChange={(e) => patch({ weight_grams: e.target.value })}
              placeholder="250"
            />
          </Field>
        </div>
        <Toggle
          switchRight
          className="border-t border-line pt-4"
          checked={draft.active}
          onChange={(active) => patch({ active })}
          label="Active"
          hint="Shown in the shop and on the manual order form."
        />
      </div>
    </Card>
  )
}
